import styled from 'styled-components';
import { darken, lighten, rgba } from 'polished';

const THEME_COLOR = '#6b63d5';

export const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px ${rgba('#000', 0.08)};
  overflow: hidden;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

export const TableHeader = styled.div`
  padding: 16px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${rgba('#000', 0.08)};
  min-height: 56px;
`;

export const TableTitle = styled.h2`
  font-size: 1.25rem;
  color: ${darken(0.4, THEME_COLOR)};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;

  i {
    font-size: 1.5rem;
    color: ${THEME_COLOR};
    flex-shrink: 0;
  }
`;

export const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

export const SearchInput = styled.div`
  position: relative;
  width: 200px;
  flex-shrink: 0;

  input {
    min-width: 100px;
    padding: 8px;
    padding-left: 32px;
    border: 1px solid ${rgba('#000', 0.1)};
    border-radius: 6px;
    font-size: 0.9rem;
    transition: all 0.2s ease;
    background: transparent;

    &:focus {
      outline: none;
      border-color: ${THEME_COLOR};
      box-shadow: 0 0 0 3px ${rgba(THEME_COLOR, 0.1)};
    }
  }

  i {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: ${rgba('#000', 0.3)};
    font-size: 1.1rem;
  }
`;

export const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  padding: 0;
  flex-shrink: 0;

  i {
    font-size: 1.2rem;
  }

  ${props => props.variant === 'primary' ? `
    background: ${THEME_COLOR};
    color: white;

    &:hover {
      background: ${darken(0.1, THEME_COLOR)};
    }

    &:disabled {
      background: ${lighten(0.2, THEME_COLOR)};
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: ${THEME_COLOR};
    border: 1px solid ${rgba(THEME_COLOR, 0.2)};

    &:hover {
      background: ${rgba(THEME_COLOR, 0.05)};
    }

    &:disabled {
      color: ${rgba(THEME_COLOR, 0.4)};
      border-color: ${rgba(THEME_COLOR, 0.1)};
      cursor: not-allowed;
    }
  `}
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Th = styled.th`
  text-align: left;
  padding: 12px 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${rgba('#000', 0.6)};
  background: ${rgba('#000', 0.02)};
  border-bottom: 1px solid ${rgba('#000', 0.08)};
  white-space: nowrap;

  &:last-child {
    text-align: right;
  }
`;

export const Td = styled.td`
  padding: 12px 10px;
  font-size: 0.875rem;
  color: ${rgba('#000', 0.8)};
  border-bottom: 1px solid ${rgba('#000', 0.08)};
  white-space: nowrap;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:last-child {
    text-align: right;
    padding-right: 16px;
  }
`;

export const Tr = styled.tr`
  transition: all 0.2s ease;

  &:hover {
    background: ${rgba(THEME_COLOR, 0.02)};
  }
`;

export const TableFooter = styled.div`
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${rgba('#000', 0.08)};
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  select {
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid ${rgba('#000', 0.1)};
    font-size: 0.875rem;
  }

  span {
    font-size: 0.875rem;
    color: ${rgba('#000', 0.6)};
  }
`;

export const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  border: 1px solid ${props => props.active ? THEME_COLOR : rgba('#000', 0.1)};
  background: ${props => props.active ? rgba(THEME_COLOR, 0.1) : 'white'};
  color: ${props => props.active ? THEME_COLOR : rgba('#000', 0.6)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${THEME_COLOR};
    color: ${THEME_COLOR};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Dialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${rgba('#000', 0.5)};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const DialogContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  overflow: hidden;
`;

export const DialogHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${rgba('#000', 0.08)};

  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: ${darken(0.4, THEME_COLOR)};
  }
`;

export const DialogBody = styled.div`
  padding: 24px;
`;

export const DialogFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${rgba('#000', 0.08)};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;